import React from "react";
import { useSelectContext } from "./Select";
import { makeStyles } from "@material-ui/core/styles";
import { ChevronDown } from "lucide-react";

const useStyles = makeStyles(() => ({
  trigger: {
    border: "1px solid #DCDCDC",
    borderRadius: "4px",
    backgroundColor: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    justifyContent: "space-between",
    transition: "border-color 0.2s",
    minWidth: "fit-content",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    "&:hover": {
      borderColor: "#BDBDBD",
    },
  },
  triggerError: {
    borderColor: "#D84949 !important",
    "&:hover": {
      borderColor: "#D84949 !important",
    },
  },
  searchInput: {
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    flex: 1,
    fontSize: "inherit",
    fontFamily: "inherit",
    cursor: "text",
  },
  searchInputPlaceholder: {
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    flex: 1,
    fontSize: "inherit",
    fontFamily: "inherit",
    cursor: "text",
    opacity: 0.6,
  },
  placeholder: {
    flex: 1,
  },
  placeholderText: {
    flex: 1,
    opacity: 0.6,
  },
  chevronButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "2px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "translateX(2px)",

    "&:hover": {
      backgroundColor: "#F0F0F0",
    },
  },
  icon: {
    // marginLeft: "8px",
    width: "16px",
    height: "16px",
    transition: "transform 0.2s",
    cursor: "pointer",
  },
  iconOpen: {
    transform: "rotate(180deg)",
  },
}));

const SelectTrigger = ({ children, style = {} }) => {
  const { toggleOpen, open, triggerRef, error, searchable, searchTerm, setSearchTerm, placeholder, value, minWidth, size, sizeMeasurements, fullwidth } = useSelectContext();
  const classes = useStyles();

  // Determine if we should show placeholder (when no value is selected)
  const showPlaceholder = !value && !children;
  const displayText = children || placeholder;

  // For searchable inputs, determine what to show as placeholder when open
  const getInputPlaceholder = () => {
    if (children && value) {
      // If there's a selected value, show it as placeholder when open
      return children;
    }
    // Otherwise show the regular placeholder
    return placeholder;
  };

  const handleClick = (e) => {
    if (!searchable) {
      toggleOpen();
    } else if (!open) {
      toggleOpen();
    }
  };

  const handleChevronClick = (e) => {
    e.stopPropagation();
    if (open) {
      toggleOpen();
    } else {
      toggleOpen();
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!open) {
      toggleOpen();
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
    if (!open) {
      toggleOpen();
    }
  };

  return (
    <div
      ref={triggerRef}
      className={`${classes.trigger} ${error ? classes.triggerError : ""}`}
      onClick={handleClick}
      data-select-trigger
      style={{
        minWidth: fullwidth ? "100%" : minWidth || "auto",
        width: fullwidth ? "100%" : "auto",
        height: sizeMeasurements.height,
        padding: sizeMeasurements.padding,
        ...style,
      }}
    >
      {searchable ? (
        <>
          <input
            type="text"
            value={open ? searchTerm : displayText}
            onChange={handleInputChange}
            onClick={handleInputClick}
            className={open || !showPlaceholder ? classes.searchInput : classes.searchInputPlaceholder}
            placeholder={getInputPlaceholder()}
            readOnly={!open}
          />
          <button type="button" className={classes.chevronButton} onClick={handleChevronClick}>
            <ChevronDown className={`${classes.icon} ${open ? classes.iconOpen : ""}`} />
          </button>
        </>
      ) : (
        <>
          <span className={showPlaceholder ? classes.placeholderText : classes.placeholder}>{displayText}</span>
          <ChevronDown className={`${classes.icon} ${open ? classes.iconOpen : ""}`} />
        </>
      )}
    </div>
  );
};

export default SelectTrigger;
